import { Check, ChevronDown, X } from 'lucide-react';
import { useState } from 'react';
import { AVAILABLE_ICONS } from '../../../constants/equipment.constants';
import { EquipmentFormData } from '../../../types/equipment.types';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

interface EquipmentFormProps {
  formData: EquipmentFormData;
  onChange: (data: EquipmentFormData) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function EquipmentForm({ formData, onChange, onSave, onCancel }: EquipmentFormProps) {
  const [isIconDropdownOpen, setIsIconDropdownOpen] = useState(false);

  const selectedIcon = AVAILABLE_ICONS.find((i) => i.name === formData.iconName);
  const SelectedIconComponent = selectedIcon?.icon;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Nom *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => onChange({ ...formData, name: e.target.value })}
            placeholder="Ex: Sonorisation"
          />
        </div>

        <div>
          <Label>Icône</Label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsIconDropdownOpen(!isIconDropdownOpen)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                {SelectedIconComponent ? (
                  <>
                    <SelectedIconComponent className="w-5 h-5 text-[#00758d]" />
                    <span className="text-sm">{selectedIcon.label}</span>
                  </>
                ) : (
                  <span className="text-sm text-gray-500">Choisir une icône</span>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            {isIconDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg">
                <div className="grid grid-cols-2 gap-1 p-2">
                  {AVAILABLE_ICONS.map((iconOption) => {
                    const IconComponent = iconOption.icon;
                    const isSelected = formData.iconName === iconOption.name;

                    return (
                      <button
                        key={iconOption.name}
                        type="button"
                        onClick={() => {
                          onChange({ ...formData, iconName: iconOption.name });
                          setIsIconDropdownOpen(false);
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100 transition-colors ${
                          isSelected ? 'bg-[#00758d]/10 border border-[#00758d]' : ''
                        }`}
                      >
                        <IconComponent className={`w-5 h-5 ${isSelected ? 'text-[#00758d]' : 'text-gray-600'}`} />
                        <span className="text-sm text-left">{iconOption.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Input
          id="description"
          value={formData.description}
          onChange={(e) => onChange({ ...formData, description: e.target.value })}
          placeholder="Description de l'équipement"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="price">Prix (0 = inclus)</Label>
          <Input
            id="price"
            type="number"
            min="0"
            value={formData.price}
            onChange={(e) => onChange({ ...formData, price: parseInt(e.target.value) || 0 })}
          />
        </div>

        <div>
          <Label htmlFor="unit">Unité</Label>
          <Input
            id="unit"
            value={formData.unit}
            onChange={(e) => onChange({ ...formData, unit: e.target.value })}
            placeholder="FCFA"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="minCapacity">Capacité min (optionnel)</Label>
          <Input
            id="minCapacity"
            type="number"
            min="0"
            value={formData.minCapacity || ''}
            onChange={(e) => onChange({ ...formData, minCapacity: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="Ex: 10"
          />
        </div>

        <div>
          <Label htmlFor="maxCapacity">Capacité max (optionnel)</Label>
          <Input
            id="maxCapacity"
            type="number"
            min="0"
            value={formData.maxCapacity || ''}
            onChange={(e) => onChange({ ...formData, maxCapacity: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="Ex: 50"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="triggersRestaurant"
          checked={formData.triggersRestaurant}
          onCheckedChange={(checked) => onChange({ ...formData, triggersRestaurant: checked as boolean })}
        />
        <Label htmlFor="triggersRestaurant" className="cursor-pointer">
          Déclenche la section restauration
        </Label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          <X className="w-4 h-4 mr-2" />
          Annuler
        </Button>
        <Button type="button" onClick={onSave} disabled={!formData.name.trim()}>
          <Check className="w-4 h-4 mr-2" />
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
